/**
 * Janus lane MV1 · M3d — the preferred-speed BURROW drag as client truth (speed_variable 'speed_burrowing') on a
 * depth-authored encounter. plans/janus-lane-mv1-teleport-burrow-squeeze-handoff-2026-09-27.md §5 S3; design
 * plans/janus-m4b-teleport-m3d-burrow-plan-2026-09-24.md §3; rulings plans/burrow-and-blind-teleport-solid-rules-2026-09-24.md.
 *
 * The browser judges the 3D polyline with shared/burrow-route.ts and prices it with priceTraversal. Routes entering solid
 * retain their 1:1 burrowing-speed price; surface-only routes use the ordinary walk's non-stacking terrain multiplier.
 * The paid prefix alone is checked for material, contact, emergence and the caller's route gates. Water and creature-space
 * pricing remain outside this scope. The end decides the Burrowed transition, saved with position by the movement commit.
 */
import { bodyInSolid, burrowPoint, burrowRouteVerdict, burrowSamples } from '../../shared/burrow-route.js';
import { burrowTier, solidContextFromEncounter, EARTH_GLIDE_ITEM_ID, BURROWS_WORKED_STONE_LAYER } from '../../shared/solid-material.js';
import { priceTraversal, clipTraversalRoute, type Point3, type TraversalPriceOptions } from './traversal-3d.js';
import { storeMovementPct } from './banked-walk.js';
import { encounterDifficultFootprints, movementClimateContext, moverCarriesItem, moverHasMovementFlag,
  stepIsDifficultTerrain, type DifficultTerrainReaders } from './difficult-terrain.js';
import { teleportBodies, type TeleportReaders } from './teleport-move.js';
import type { PlainWalkResult, WalkPoint } from './plain-walk.js';
import { statusItemIdOf } from './squeeze-at-rest.js';

type Row = Record<string, unknown>;
export const SPEED_BURROWING_TYPE_ID = 'ACFB413D-15A8-835B-C821-9E101B6C4F60';
export const BURROWED_ITEM_ID = '6436D343-5BCA-4147-8915-47A7C648977E';
const record = (value: unknown): Row | null => value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Row : null;
const bucket = (row: Row, name: string): Row => record(row[name]) ?? {};
const scalar = (value: unknown): unknown => Array.isArray(value) ? value[0] : value;
const upper = (value: unknown): string => String(scalar(value) ?? '').trim().toUpperCase();
const round3 = (value: number): number => Number(value.toFixed(3));

export type BurrowScope = { supported: true; walk: PlainWalkResult }
  | { supported: false; refused: true; message: string }
  | { supported: false; refused?: false; reason: string; detail?: Record<string, unknown> };
type Gates = (planar: Array<[number, number]>) => { supported: true } | { supported: false; refused?: boolean; reason?: string; message?: string; detail?: Record<string, unknown> };

/** The 3D polyline a burrow drag commits: the scene's z-bearing route when every point carries z, else the straight line. */
export function burrowPolyline(origin: WalkPoint, destination: WalkPoint, route: ReadonlyArray<readonly number[]>): Point3[] | null {
  // A z-bearing route's INTERIOR points must be z-bearing and strictly numeric (burrowPoint — PHP is_numeric): a malformed
  // point refuses the route instead of being coerced to 0 (Codex M3d pass 2). The endpoints are always the saved origin and
  // the committed destination — the parent entry re-writes the last point planar (movement-truth.ts tryCommitPlainWalk) —
  // so they are never parsed. A planar (or absent) route is the straight line.
  const zBearing = route.length >= 2 && route.some(p => p.length >= 3);
  let points: Point3[];
  if (zBearing) {
    const parsed = route.map((p, i) => (i === 0 || i === route.length - 1 ? [0, 0, 0] as Point3 : burrowPoint(p)));
    if (parsed.some(p => p === null)) return null;
    points = parsed as Point3[];
  } else {
    points = [[origin.x, origin.y, origin.z], [destination.x, destination.y, destination.z]];
  }
  if (points.length > 512) return null;
  points[0] = [origin.x, origin.y, origin.z];
  points[points.length - 1] = [destination.x, destination.y, destination.z];
  return points;
}

export function resolveBurrowScope(
  input: { actorId: string; origin: WalkPoint; destination: WalkPoint; route: ReadonlyArray<readonly number[]>; force: boolean },
  encounter: Row, caster: Row, read: TeleportReaders & DifficultTerrainReaders, gates: Gates, notYourTurn: string, noMovement: string,
): BurrowScope {
  const no = (reason: string, detail?: Record<string, unknown>): BurrowScope => (detail ? { supported: false, reason, detail } : { supported: false, reason });
  const refuse = (message: string): BurrowScope => ({ supported: false, refused: true, message });
  const { origin, destination } = input;
  if (input.force || !input.actorId || ![origin.x, origin.y, origin.z, destination.x, destination.y, destination.z].every(Number.isFinite)) return no('route-or-movement-mode');
  if (Math.hypot(destination.x - origin.x, destination.y - origin.y, destination.z - origin.z) < 0.01) return no('route-or-movement-mode');
  const ec = bucket(caster, 'encounter_creatures');
  if (upper(ec.preferred_speed_type) !== SPEED_BURROWING_TYPE_ID) return no('preferred-speed', { preferred: upper(ec.preferred_speed_type) });
  const inCombat = read.inCombat(encounter, caster);
  if (inCombat && upper(read.value(encounter, 'current_encounter_creature_id_turn')) !== upper(input.actorId)) return refuse(notYourTurn);
  let shape = bucket(encounter, 'encounters').encounter_shape ?? encounter.encounter_shape;
  if (typeof shape === 'string') { try { shape = JSON.parse(shape) as unknown; } catch { return no('shape'); } }
  const boxes = record(shape)?.boxes;
  if (!Array.isArray(boxes) || boxes.length === 0) return no('burrow-legacy-2d');
  const level = bucket(encounter, 'encounters').encounter_water_level ?? encounter.encounter_water_level;
  const bodiesOfWater = record(shape)?.waterBodies;
  if ((level !== undefined && level !== null && level !== '') || (Array.isArray(bodiesOfWater) && bodiesOfWater.length > 0)) return no('water');
  const base = Number(scalar(read.value(caster, 'speed_burrowing')) ?? 0);
  if (!(base > 0)) return no('burrowing-speed');
  const polyline = burrowPolyline(origin, destination, input.route);
  if (!polyline) return no('route-or-movement-mode');
  const geometry = read.geometry(caster);
  const size = geometry ? (geometry.rulesDiameter ?? geometry.diameter) : NaN;
  if (!(size > 0)) return no('mover-geometry');
  const tier = burrowTier({ burrowSpeedFt: base, earthGlide: moverCarriesItem(caster, read, EARTH_GLIDE_ITEM_ID), worksStone: moverHasMovementFlag(caster, read, BURROWS_WORKED_STONE_LAYER) });
  const ctx = solidContextFromEncounter(encounter, read.statuses);
  const bodies = teleportBodies(encounter, input.actorId, read);
  if (!bodies) return no('other-geometry');
  // Price FIRST (Codex M3d review): an illegal or floating stretch beyond what the budget reaches must not veto the paid
  // prefix. Then the rule judges EXACTLY the route that will be committed — the clipped prefix ending on the 3 dp stop.
  const samples = burrowSamples(polyline);
  if (samples.length < 2) return no('route-or-movement-mode');
  let committed: Point3[] = polyline;
  let pricing: { before: { movement_pct: number; aps: number }; speed_cost: number; movement_pct: number; aps: number } | null = null;
  let difficultSteps: number | undefined;
  let truncated: PlainWalkResult['truncated'];
  if (inCombat) {
    const rawBank = scalar(ec.movement_pct), rawAps = scalar(ec.aps);
    const bank = rawBank === undefined || rawBank === null || rawBank === '' ? 0 : Number(rawBank);
    const aps = rawAps === undefined || rawAps === null || rawAps === '' ? 0 : Number(rawAps);
    if (!Number.isFinite(bank) || bank < 0) return no('bank-shape');
    if (!Number.isSafeInteger(aps) || aps < 0) return no('aps-shape');
    const options: TraversalPriceOptions = { speedType: 'speed_burrowing', baseSpeedFt: base, swimSpeedFt: 0, movementPct: bank, availableAps: aps, water: null, wadeThresholdFt: 2.5 };
    let price = priceTraversal(samples, options);
    if (!price) return no('combat-budget');
    const paidRoute = (stopIndex: number): Point3[] => {
      if (stopIndex === samples.length - 1) return polyline;
      const s = samples[stopIndex]!;
      return clipTraversalRoute(polyline, s, [round3(s.x), round3(s.y), round3(s.z)]);
    };
    // Preserve the price of every previously committed dig. Classify its exact rounded paid prefix, so an underground
    // suffix beyond the budget cannot exempt a surface walk from terrain. Repricing can only shorten that surface prefix.
    const paidSamples = price.complete ? samples : burrowSamples(paidRoute(price.stopIndex));
    if (!paidSamples.some(s => bodyInSolid(ctx, s, size / 2, size))) {
      const footprints = encounterDifficultFootprints(encounter);
      const climate = movementClimateContext(encounter, caster, read);
      price = priceTraversal(samples, { ...options,
        multiplierAt: s => stepIsDifficultTerrain(s.x, s.y, s.z, size / 2, footprints, climate) ? 2 : 1 });
      if (!price) return no('combat-budget');
      difficultSteps = price.difficult_steps;
    }
    pricing = { before: { movement_pct: bank, aps }, speed_cost: price.speedCostFt, movement_pct: storeMovementPct(price.remainingPct), aps: aps - price.apsSpent };
    if (!price.complete) {
      if (price.stopIndex === 0) return refuse(price.haltMessage ?? noMovement);
      committed = paidRoute(price.stopIndex);
      truncated = { requested: { ...destination }, route: polyline, paid_ft: price.speedCostFt, message: price.haltMessage ?? noMovement };
    }
  }
  // The route gates judge the COMMITTED (paid) prefix's planar projection (Codex M3d pass 2: an unpaid suffix's zone or
  // watcher must not hand back a legal paid prefix).
  const gate = gates(committed.map(p => [p[0], p[1]]));
  if (!gate.supported) return gate.refused ? refuse(String(gate.message ?? '')) : no(String(gate.reason ?? 'gates'), gate.detail);
  const verdict = burrowRouteVerdict(ctx, bodies, tier, committed, size / 2, size);
  if (!verdict.ok) return 'refused' in verdict ? refuse(verdict.refused) : no(verdict.handback, { at: verdict.at });
  const held = read.statuses(caster).filter(status => statusItemIdOf(status) === BURROWED_ITEM_ID);
  const end = committed[committed.length - 1]!;
  let walk: PlainWalkResult = { schema: 'janus.movement.m1a.v1', actor_id: input.actorId, origin: { ...origin }, destination: { x: end[0], y: end[1], z: end[2] },
    route: committed.map(p => [p[0], p[1], p[2]] as [number, number, number]), updates: { x_pos: end[0], y_pos: end[1], z_pos: end[2] },
    speed_cost: 0, traversal: { kind: 'burrow', samples: verdict.samples.length,
      ...(difficultSteps !== undefined ? { difficult_steps: difficultSteps } : {}) } };
  if (pricing) {
    walk = { ...walk, before: pricing.before, speed_cost: pricing.speed_cost, updates: { ...walk.updates, movement_pct: pricing.movement_pct, aps: pricing.aps },
      ...(truncated ? { truncated } : {}) };
  }
  const transition = verdict.inside && held.length === 0 ? 'enter' : !verdict.inside && held.length > 0 ? 'leave' : 'none';
  walk.burrow = { transition, inside: verdict.inside, tier };
  return { supported: true, walk };
}
